import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import {
  Search, Check, Ban, Trash2, Edit, ExternalLink, Image as ImageIcon,
  SquareCheck, Square
} from 'lucide-react';
import { Chevron, InfoField, EmptyState, listingStatusColors } from './shared';
import { Package } from 'lucide-react';

export function ListingsTab({
  listings, listingSearch, setListingSearch, listingStatusFilter, setListingStatusFilter,
  selectedListings, toggleSelectListing, selectAllListings, bulkListingAction,
  updateListingStatus, deleteListing, setEditListing, setEditForm
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input placeholder="Rechercher par titre, vendeur..." value={listingSearch} onChange={(e) => setListingSearch(e.target.value)} className="pl-9" data-testid="listing-search-input" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['', 'active', 'suspended', 'pending'].map(f => (
            <Button key={f} variant={listingStatusFilter === f ? 'default' : 'outline'} size="sm" onClick={() => setListingStatusFilter(f)} className={listingStatusFilter === f ? 'bg-[#1F3F72] text-white' : ''} data-testid={`listing-filter-${f || 'all'}`}>
              {f === '' ? 'Toutes' : f === 'active' ? 'Actives' : f === 'suspended' ? 'Suspendues' : 'En attente'}
            </Button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={selectAllListings} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300" data-testid="select-all-listings">
            {selectedListings.size === listings.length && listings.length > 0 ? <SquareCheck className="w-5 h-5 text-[#1F3F72]" /> : <Square className="w-5 h-5" />}
          </button>
          <p className="text-xs text-neutral-500">{listings.length} annonce(s)</p>
        </div>
        {selectedListings.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-[#1F3F72] dark:text-blue-300">{selectedListings.size} selectionnee(s)</span>
            <Button size="sm" variant="outline" onClick={() => bulkListingAction('activate')} className="border-emerald-300 text-emerald-600 text-xs" data-testid="bulk-activate-listings"><Check className="w-3 h-3 mr-1" /> Activer</Button>
            <Button size="sm" variant="outline" onClick={() => bulkListingAction('suspend')} className="border-amber-300 text-amber-600 text-xs" data-testid="bulk-suspend-listings"><Ban className="w-3 h-3 mr-1" /> Suspendre</Button>
            <Button size="sm" variant="outline" onClick={() => bulkListingAction('delete')} className="border-red-300 text-red-600 text-xs" data-testid="bulk-delete-listings"><Trash2 className="w-3 h-3 mr-1" /> Supprimer</Button>
          </div>
        )}
      </div>
      <div className="space-y-3">
        {listings.length === 0 ? <EmptyState icon={Package} text="Aucune annonce" /> : listings.map((l) => (
          <div key={l.id} className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 overflow-hidden" data-testid={`admin-listing-${l.id}`}>
            <div className="p-4 flex items-center gap-3">
              <button onClick={(e) => { e.stopPropagation(); toggleSelectListing(l.id); }} className="text-neutral-400 hover:text-neutral-600 shrink-0">
                {selectedListings.has(l.id) ? <SquareCheck className="w-5 h-5 text-[#1F3F72]" /> : <Square className="w-5 h-5" />}
              </button>
              <div className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setExpandedId(expandedId === l.id ? null : l.id)}>
                <div className="w-14 h-14 rounded-lg bg-neutral-100 dark:bg-neutral-700 overflow-hidden shrink-0">
                  {l.images?.[0] ? <img src={l.images[0]} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-neutral-300 m-auto mt-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-neutral-900 dark:text-white truncate">{l.title}</p>
                  <p className="text-xs text-neutral-500 truncate">{l.company_name || l.seller_name} {l.seller_verified && '\u2713'}</p>
                </div>
                <p className="text-sm font-bold text-[#1F3F72] dark:text-[#93b4e0] shrink-0">{l.price?.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p>
                <Badge className={`text-[10px] shrink-0 ${listingStatusColors[l.status] || ''}`}>{l.status}</Badge>
                <Chevron open={expandedId === l.id} />
              </div>
            </div>
            {expandedId === l.id && (
              <div className="border-t dark:border-neutral-700 px-4 py-5 bg-neutral-50/50 dark:bg-neutral-850">
                {l.images?.length > 0 && (
                  <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                    {l.images.map((img, i) => <img key={img} src={img} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0 border dark:border-neutral-600" />)}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5 text-sm">
                  <InfoField label="Titre" value={l.title} />
                  <InfoField label="Prix" value={l.price?.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} />
                  <InfoField label="Secteur" value={l.sector || '-'} />
                  <InfoField label="Categorie" value={l.category || '-'} />
                  <InfoField label="Etat" value={l.condition || '-'} />
                  <InfoField label="Vendeur" value={`${l.seller_name || ''} (${l.company_name || '-'})`} />
                  <InfoField label="Cree le" value={l.created_at ? new Date(l.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'} />
                  <InfoField label="Boosts actifs" value={l.boosts?.filter(b => new Date(b.expires_at) > new Date()).length || 0} />
                </div>
                {l.description && <div className="mb-4 p-3 bg-white dark:bg-neutral-700 rounded-lg border dark:border-neutral-600 text-sm text-neutral-700 dark:text-neutral-300 max-h-32 overflow-y-auto">{l.description}</div>}
                <div className="flex flex-wrap gap-2 pt-3 border-t dark:border-neutral-700">
                  <Link to={`/annonces/${l.id}`} target="_blank"><Button size="sm" variant="outline"><ExternalLink className="w-4 h-4 mr-1" /> Voir</Button></Link>
                  <Button size="sm" variant="outline" onClick={() => { setEditListing(l.id); setEditForm({ title: l.title, description: l.description || '', price: l.price, sector: l.sector || '', category: l.category || '', condition: l.condition || '' }); }} data-testid={`edit-listing-${l.id}`}><Edit className="w-4 h-4 mr-1" /> Modifier</Button>
                  {l.status !== 'active' && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => updateListingStatus(l.id, 'active')} data-testid={`approve-listing-${l.id}`}><Check className="w-4 h-4 mr-1" /> Activer</Button>}
                  {l.status !== 'suspended' && <Button size="sm" variant="outline" className="border-amber-300 text-amber-600 hover:bg-amber-50" onClick={() => updateListingStatus(l.id, 'suspended')} data-testid={`suspend-listing-${l.id}`}><Ban className="w-4 h-4 mr-1" /> Suspendre</Button>}
                  {deleteConfirmId === l.id ? (
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => { deleteListing(l.id); setDeleteConfirmId(null); }} data-testid={`confirm-delete-listing-${l.id}`}><Trash2 className="w-4 h-4 mr-1" /> Confirmer</Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteConfirmId(null)}>Annuler</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirmId(l.id)} data-testid={`delete-listing-${l.id}`}><Trash2 className="w-4 h-4 mr-1" /> Supprimer</Button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
